import { ModelTransformer } from '../common/common.model';

export enum NotificationPriority {
    NONE = 0,
    LOW,
    MEDIUM,
    HIGH,
    CRITICAL,
}

export enum NotificationCategoryList {
    ANNOUNCEMENT = 2,
    ALERT = 3,
    COACHING = 4,
    FOLLOWUP = 5,
}

export interface ExtNotificationCategory {
    categoryId: number;
    defaultName: string;
    textId: number;
}

export interface ExtNotificationLink {
    notificationLinkId: number;
    notificationId: number;
    name: string;
    url: string;
    displayOrder: number;
}

export interface ExtNotification {
    notificationId: number;
    categoryId: number;
    priorityId: number;
    requiredAcknowlegement: boolean;
    announcementId: number;
    toUserId: number;
    fromUserId: number;
    body: string;
    subject: string;
    readOn: string; // date
    acknowledgedOn: string; // date
    deletedOn: string; // date
    createdBy: number;
    createdOn: string; // date
    notificationLink: ExtNotificationLink[];
}

export interface NotificationCategory {
    id: number;
    defaultName: string;
}

export interface NotificationLink {
    id: number;
    name: string;
    url: string;
}

export class Notification {
    id: number;
    categoryId: number;
    priorityId: NotificationPriority;
    requiresAcknowledgement: boolean;
    body: string;
    subject: string;
    readOn: Date;
    acknowledgedOn: Date;
    deletedOn: Date;
    createdOn: Date;
    notificationLinks: NotificationLink[];
    wasRead: boolean;
    wasAcknowledged: boolean;
    isDeleted: boolean;

    merge(that: Notification): void {
        this.id = that.id != null ? that.id : this.id;
        this.categoryId =
            that.categoryId != null ? that.categoryId : this.categoryId;
        this.priorityId =
            that.priorityId != null ? that.priorityId : this.priorityId;
        this.requiresAcknowledgement =
            that.requiresAcknowledgement != null
                ? that.requiresAcknowledgement
                : this.requiresAcknowledgement;
        this.body = that.body != null ? that.body : this.body;
        this.subject = that.subject != null ? that.subject : this.subject;
        this.readOn = new Date(
            that.readOn != null ? that.readOn.getTime() : this.readOn.getTime(),
        );
        this.acknowledgedOn = new Date(
            that.acknowledgedOn != null
                ? that.acknowledgedOn.getTime()
                : this.acknowledgedOn.getTime(),
        );
        this.deletedOn = new Date(
            that.deletedOn != null
                ? that.deletedOn.getTime()
                : this.deletedOn.getTime(),
        );
        this.createdOn = new Date(
            that.createdOn != null
                ? that.createdOn.getTime()
                : this.createdOn.getTime(),
        );
        this.notificationLinks =
            that.notificationLinks != null
                ? that.notificationLinks
                : this.notificationLinks;
        this.wasRead = that.wasRead != null ? that.wasRead : this.wasRead;
        this.wasAcknowledged =
            that.wasAcknowledged != null
                ? that.wasAcknowledged
                : this.wasAcknowledged;
        this.isDeleted =
            that.isDeleted != null ? that.isDeleted : this.isDeleted;
    }
}

export class NotificationCategoryModelTransformer
    implements
        ModelTransformer<NotificationCategory[], ExtNotificationCategory[]>
{
    transform(model: ExtNotificationCategory[]): NotificationCategory[] {
        const transformed: NotificationCategory[] = [];
        model.forEach((item) => {
            transformed.push({
                id: item.categoryId,
                defaultName: item.defaultName,
            });
        });
        return transformed;
    }
}

export class NotificationLinkModelTransformer
    implements ModelTransformer<NotificationLink[], ExtNotificationLink[]>
{
    transform(model: ExtNotificationLink[]): NotificationLink[] {
        const transformed: NotificationLink[] = [];
        model.sort((a, b) => a.displayOrder - b.displayOrder);
        model.forEach((item) => {
            transformed.push({
                id: item.notificationLinkId,
                name: item.name,
                url: item.url,
            });
        });
        return transformed;
    }
}

export class NotificationModelTransformer
    implements ModelTransformer<Notification[], ExtNotification[]>
{
    transform(model: ExtNotification[]): Notification[] {
        const transformed: Notification[] = [];
        model.forEach((item) => {
            const notificationLinks =
                new NotificationLinkModelTransformer().transform(
                    item.notificationLink,
                );
            const readOn = new Date(item.readOn || '');
            const acknowledgedOn = new Date(item.acknowledgedOn || '');
            const deletedOn = new Date(item.deletedOn || '');
            const m = new Notification();
            m.id = item.notificationId;
            m.categoryId = item.categoryId;
            m.priorityId = item.priorityId;
            m.requiresAcknowledgement = item.requiredAcknowlegement;
            m.body = item.body;
            m.subject = item.subject;
            m.readOn = readOn;
            m.acknowledgedOn = acknowledgedOn;
            m.deletedOn = deletedOn;
            m.createdOn = new Date(item.createdOn);
            m.notificationLinks = notificationLinks;
            m.wasRead = !isNaN(readOn.getTime());
            m.wasAcknowledged = !isNaN(acknowledgedOn.getTime());
            m.isDeleted = !isNaN(deletedOn.getTime());
            transformed.push(m);
        });
        return transformed.sort(
            (a, b) => b.createdOn.getTime() - a.createdOn.getTime(),
        );
    }
}

export class ViewModel {
    notifications: Notification[];
    notificationAcks: any;

    constructor() {
        this.notificationAcks = {};
    }
}
